import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, Stack, Tab} from '@mui/material';
import {Navigate, useParams, useSearchParams} from 'react-router-dom';
import {useSeatingPlanStore} from '../../api/SeatingPlanStore';
import {Page} from '../../components/page/Page';
import {AppPages, usePageUrl} from "../../app-layout/AppPages";
import {TabContext, TabList, TabPanel} from "@mui/lab";
import {neighbourhoodsTab} from "./NeighbourhoodListTab";
import {SeatingPlanModel} from "./SeatingPlanModel";
import {useNeighbourhoodStore} from "../../api/NeighbourhoodStore";
import {Loading} from "../../components/loading-data/LoadingData";
import {useEmployeeVisitStore} from "../../api/EmployeeVisitStore";
import {RemoteValue} from "../../util/RemoteValue";
import {usePageActionHandler} from "../../components/page-action/PageActionHandler";
import {PageActionToolbar} from "../../components/page-action/PageActionToolbar";
import {SeatingPlanTab} from "./SeatingPlanTab";
import {summaryTab} from "./SummaryTab";
import {moveEmployeesTab} from "./EmployeesTreeTab";
import {treemapTab} from "./CompanyTreemapTab";
import {useDeskStore} from "../../api/DeskStore";
import {desksTab} from "./DesksTab";
import {useFloorStore} from "../../api/FloorStore";
import {HeaderBreadcrumb, HeaderBreadcrumbs} from "../../components/page/HeaderBreadcrumbs";
import {floorsTab} from "./FloorsTab";
import {SeatingPlanTabCount} from "./SeatingPlanTabLabel";

const tabs: SeatingPlanTab<any>[] = [
    summaryTab,
    moveEmployeesTab,
    floorsTab,
    desksTab,
    neighbourhoodsTab,
    treemapTab,
];

function SeatingPlanPageImpl({model}: {
    model: SeatingPlanModel;
}) {
    const plan = model.plan;
    const [searchParams, setSearchParams] = useSearchParams();
    const [tab, setTab] = useState<SeatingPlanTab<any>['key']>(searchParams.get('tab') || tabs[0].key);
    const filterHistoryRef = useRef<Record<SeatingPlanTab<any>["key"], { [p: string]: string }>>({})
    const pageUrl = usePageUrl();

    useEffect(() => {
        const newTab = searchParams.get('tab') || tabs[0].key;
        if (newTab !== tab) {
            setTab(newTab);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    useEffect(() => {
        const {tab: prevTab, ...prevSearchParams} = Object.fromEntries(searchParams.entries()) || {tab: tabs[0].key};
        if (tab !== prevTab) {
            const filterHistory = filterHistoryRef.current;
            const restoredFilter = filterHistory[tab] || {};
            filterHistoryRef.current = {...filterHistory, [prevTab]: prevSearchParams};
            setSearchParams({...restoredFilter, tab}, {replace: false});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, filterHistoryRef]);


    const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: SeatingPlanTab<any>['key']) => {
        setTab(newValue);
    }, [setTab]);

    const breadcrumbs: HeaderBreadcrumb[] = useMemo(() => [
        HeaderBreadcrumbs.link('Seating Plans', pageUrl(AppPages.seatingPlanList)),
        HeaderBreadcrumbs.text(plan.name || ''),
    ], [plan, pageUrl]);

    const actionHandler = usePageActionHandler();
    const currentTab = useMemo(() => {
        return tabs.find(it => it.key === tab);
    }, [tab]);

    return (
        <Page
            header={<HeaderBreadcrumbs breadcrumbs={breadcrumbs}/>}
            description={plan.description}
            actions={currentTab && (
                <PageActionToolbar
                    context={model}
                    pageActions={currentTab.actions}
                    actionHandler={actionHandler}
                />
            )}
        >
            <TabContext value={tab}>
                <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                    <TabList onChange={handleTabChange} aria-label="seating plan tabs">
                        {tabs.map(curTab => (
                            <Tab
                                key={curTab.key}
                                value={curTab.key}
                                label={curTab.label(model, curTab.key === tab)}
                            />
                        ))}
                    </TabList>
                </Box>
                {tabs.map(tab => {
                    const TabContent = tab.content;
                    return (
                        <TabPanel key={tab.key} value={tab.key} sx={{px: 0, py: 2}}>
                            <TabContent model={model} actionHandler={actionHandler}/>
                        </TabPanel>
                    );
                })}
            </TabContext>
        </Page>
    );
}

export default function SeatingPlanPage() {
    const {planId} = useParams();
    const pageUrl = usePageUrl();

    const seatingPlanStore = useSeatingPlanStore();
    const seatingPlans = seatingPlanStore.remoteValue;
    const employeeVisits = useEmployeeVisitStore(store => store.remoteValue);
    const neighbourhoods = useNeighbourhoodStore(state => state.remoteValue);
    const desks = useDeskStore(state => state.remoteValue);
    const floors = useFloorStore(state => state.remoteValue);

    const model = useMemo(
        () => {
            const id = Number(planId);
            const plan = seatingPlans.map(plans =>
                plans.find(plan => plan.id === id)
            );

            return RemoteValue
                .combine({plan, neighbourhoods, employeeVisits, desks, floors})
                .map(({plan, neighbourhoods, employeeVisits, desks, floors}) => {
                    if (!plan) return null;

                    return SeatingPlanModel.createModel(
                        plan,
                        neighbourhoods,
                        desks,
                        floors,
                        employeeVisits,
                        seatingPlanStore,
                    );
                });
        },
        [planId, seatingPlans, neighbourhoods, employeeVisits, desks, floors, seatingPlanStore],
    );


    return (
        <Loading value={model}>{resolved =>
            resolved ? (
                <SeatingPlanPageImpl model={resolved}/>
            ) : (
                <Navigate to={pageUrl(AppPages.seatingPlanList)} replace/>
            )
        }</Loading>
    );
}
